import type { ProviderProfileTranslation } from './i18n';

export interface ProviderProfile {
  id: number;
  userId: number;
  displayName: string;
  bio: string | null;
  photoUrl: string | null;
  defaultBookingMode: BookingMode;
  defaultDepositAmount: number | null;
  tenantSlotStepMinutes: number;
  tenantDefaultServiceDurationMinutes: number;
  googleCalendarConnected: boolean;
  active: boolean;
  enabledByOwner: boolean;
  createdAt: string;
  updatedAt: string;
  translations?: ProviderProfileTranslation[];
}

export interface ProviderStats {
  todayTotal: number;
  todayPending: number;
  todayConfirmed: number;
  todayCompleted: number;
  todayCancelled: number;
  todayRevenue: number;
}

export type TodayStatusType = 'WORKING' | 'OFF' | 'NOT_CONFIGURED';

export interface TodayStatus {
  todayStatus: TodayStatusType;
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  nextAvailableSlot: string | null;
}

export type PaymentStatus = 'UNPAID' | 'PAID';

export interface TodayBooking {
  id: number;
  startTime: string;
  endTime: string;
  clientName: string;
  serviceName: string;
  durationMinutes: number;
  price: number;
  status: BookingStatus;
  customerType?: 'REGISTERED' | 'GUEST';
  paymentStatus?: PaymentStatus | null;
}

export interface ProviderService {
  id: number;
  serviceProviderId: number;
  serviceTypeId: number | null;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number;
  bookingMode: BookingMode;
  depositAmount: number | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BookingMode = 'MANUAL_CONFIRM' | 'FREE_AUTO_CONFIRM';

export interface ServiceType {
  id: number;
  tenantId: number;
  name: string;
  description: string | null;
  active: boolean;
}

export type BookingStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'COMPLETED'
  | 'CANCELED_BY_USER'
  | 'CANCELED_BY_SERVICE_PROVIDER'
  | 'EXPIRED'
  | 'REFUND_REQUESTED'
  | 'REVERSED_OK'
  | 'NO_SHOW';

export type CreatedByRole = 'SERVICE_PROVIDER' | 'CUSTOMER';

export type CustomerAttendanceStatus = 'CONFIRMED' | 'DECLINED';

export const NO_SHOW_REASONS = [
  'Mijoz kelmadi',
  'Mijoz bilan kelisholmadik',
  'Boshqa sabab',
] as const;

export type NoShowReason = (typeof NO_SHOW_REASONS)[number];

export const BOOKING_DECLINE_REASONS = [
  'PROVIDER_BUSY',
  'TIME_NOT_SUITABLE',
  'OTHER',
] as const;

export type BookingDeclineReason = (typeof BOOKING_DECLINE_REASONS)[number];

export interface Booking {
  id: number;
  serviceProviderId: number;
  serviceId: number;
  customerId: number;
  startTime: string;
  endTime: string;
  status: BookingStatus;
  bookingMode: BookingMode;
  depositAmount: number | null;
  totalAmount: number | null;
  customerNotes: string | null;
  cancellationReason: string | null;
  cancellationReasonText: string | null;
  customerAttendanceStatus: CustomerAttendanceStatus | null;
  confirmedAt: string | null;
  canceledAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  paymentStatus: PaymentStatus | null;
  createdByRole?: CreatedByRole | null;
  // Extended fields from provider search API
  clientName?: string;
  clientTelegramId?: number;
  clientPhone?: string;
  serviceName?: string;
  durationMinutes?: number;
  customerType?: 'REGISTERED' | 'GUEST';
  notificationSent?: boolean;
}

export type RangeKey = 'today' | 'week' | 'month';

export type StatusFilterKey =
  | 'ALL'
  | 'PENDING'
  | 'CONFIRMED'
  | 'COMPLETED'
  | 'CANCELED_BY_USER'
  | 'CANCELED_BY_SERVICE_PROVIDER';

export interface BookingsSummary {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  canceled: number;
}

export type BookingsErrorKind = 'conflict' | 'offline' | 'network' | 'unknown';

export interface BookingPage {
  content: Booking[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface WorkingHours {
  id: number;
  serviceProviderId: number;
  dayOfWeek: number;
  dayName: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
}

export interface ScheduleBreak {
  id: number;
  serviceProviderId: number;
  dayOfWeek: number;
  dayName: string;
  startTime: string;
  endTime: string;
}

export interface ScheduleException {
  id: number;
  serviceProviderId: number;
  exceptionDate: string;
  reason: string | null;
  dayOfWeek: DayOfWeek;
  startTime: string | null;
  endTime: string | null;
  fullDay: boolean;
}

export interface WeeklySchedule {
  serviceProviderId: number;
  providerName: string;
  workingHoursByDay: Record<number, WorkingHours | null>;
  breaksByDay: Record<number, ScheduleBreak[]>;
  upcomingExceptions: ScheduleException[];
}

// ─── New Schedule API types ──────────────────────────────────────────────────

export type DayOfWeek =
  | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY'
  | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface ScheduleDayEntry {
  dayOfWeek: DayOfWeek;
  enabled: boolean;
  startTime: string | null;
  endTime: string | null;
  workingHoursId: number | null;
}

export interface ScheduleBreakEntry {
  id: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export type OffDateMode = 'full' | 'partial';

export interface ScheduleOffDate {
  id: number;
  date: string;
  reason: string | null;
  dayOfWeek: DayOfWeek;
}

export interface ProviderSchedule {
  availabilityEnabled: boolean;
  scheduleConfigured: boolean;
  weeklySchedule: ScheduleDayEntry[];
  breaks: ScheduleBreakEntry[];
  offDates: ScheduleOffDate[];
}

// ─── Analytics ───────────────────────────────────────────────────────────────

export type AnalyticsPeriod = 'week' | 'month' | 'quarter' | 'year';

export interface AnalyticsRevenuePoint {
  label: string;
  value: number;
}

export interface AnalyticsTopService {
  id: number;
  name: string;
  revenue: number;
  bookings: number;
}

export interface AnalyticsTopCustomer {
  id: number;
  name: string;
  totalSpent: number;
  bookings: number;
}

export interface AnalyticsPeakHour {
  hour: string;
  bookings: number;
}

export interface AnalyticsData {
  totalRevenue: number;
  prevRevenue: number;
  totalBookings: number;
  completedBookings: number;
  confirmedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  completionRate: number;
  avgRevenue: number;
  revenueByPoint: AnalyticsRevenuePoint[];
  topServices: AnalyticsTopService[];
  topCustomers: AnalyticsTopCustomer[];
  peakHours: AnalyticsPeakHour[];
}
